import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// ── Module codes (10 system modules) ─────────────────────────────────────────

export const ModuleCodeSchema = z.enum([
  'MILK_MANAGEMENT',
  'FEED_PREP',
  'ADMINISTRATION',
  'INVENTORY',
  'REPORTS',
  'SETTINGS',
  'DONOR_PORTAL',
  'RECALL',
  'PATIENT_MOTHER_DONOR_MGMT',
  'AUDIT_LOG',
  'USER_MANAGEMENT',
]);
export type ModuleCode = z.infer<typeof ModuleCodeSchema>;

// ── Role codes (16 system roles — includes ENTERPRISE_ADMIN and AUDITOR) ─────

export const SystemRoleCodeSchema = z.enum([
  'NURSE',
  'PREP_TECHNICIAN',
  'NICU_MANAGER',
  'CLINICAL_ADMIN',
  'TECHNICAL_ADMIN',
  'ENTERPRISE_ADMIN',
  'INVENTORY_MANAGER',
  'INTAKE_STAFF',
  'MILK_BANK_OPERATOR',
  'DONOR_COORDINATOR',
  'DIETITIAN',
  'LACTATION',
  'MILK_PREP_TECHNICIAN',
  'UNIT_SECRETARY',
  'ANCILLARY_STAFF',
  'AUDITOR',
]);
export type SystemRoleCode = z.infer<typeof SystemRoleCodeSchema>;

// ── Module access map: { module_code → boolean } ─────────────────────────────
// Keys are validated against the 10 module codes.

export const ModuleAccessMapSchema = z.record(ModuleCodeSchema, z.boolean());
export type ModuleAccessMap = z.infer<typeof ModuleAccessMapSchema>;

// ── Role item (from DynamoDB ROLE table) ─────────────────────────────────────

export const RoleDefinitionSchema = z.object({
  role_code: SystemRoleCodeSchema,
  role_name: z.string(),
  role_subtitle: z.string().optional(),
  role_description: z.string().optional(),
  applicable_scope: z.enum(['HOSPITAL', 'ENTERPRISE']).optional(),
  is_active: z.boolean().optional(),
  is_default: z.boolean().optional(),
  is_matrix_editable: z.boolean().optional(),
  default_module_access: ModuleAccessMapSchema,
  permissions_set: z.record(z.string(), z.unknown()).optional(),
});
export type RoleDefinition = z.infer<typeof RoleDefinitionSchema>;

// ── Full matrix: { role_code → { module_code → boolean } } ──────────────────

export const RoleModuleMatrixSchema = z.record(SystemRoleCodeSchema, ModuleAccessMapSchema);
export type RoleModuleMatrix = z.infer<typeof RoleModuleMatrixSchema>;

// ── GET response for role-module-matrix endpoint ─────────────────────────────

export const RoleModuleMatrixResponseSchema = z.object({
  hospital_id: uuidSchema,
  matrix: RoleModuleMatrixSchema,
  source: z.enum(['hospital', 'default']),
  updated_by: z.string().optional(),
  updated_at: z.string().optional(),
});
export type RoleModuleMatrixResponse = z.infer<typeof RoleModuleMatrixResponseSchema>;

// ── PUT request body for saving/updating role-module-matrix ──────────────────

export const SaveRoleModuleMatrixRequestSchema = z.object({
  matrix: RoleModuleMatrixSchema,
  updated_by: uuidSchema,
});
export type SaveRoleModuleMatrixRequest = z.infer<typeof SaveRoleModuleMatrixRequestSchema>;

// ── Feature codes — matches seed-rbac-xlsx.json ───────────────────────────────

export const FeatureCodeSchema = z.enum([
  // PATIENT_MANAGEMENT
  'patient.profile_communication',
  'patient.mother',
  // INVENTORY
  'inv.donor_milk_receive',
  'inv.mother_milk_receive',
  'inv.formulary',
  // FEED_MANAGEMENT — Order Creation
  'feed.order_donor_only',
  'feed.order_donor_mother',
  'feed.order_full',
  'feed.order_product',
  // FEED_MANAGEMENT — Feed Preparation
  'feed.feed_prep_donor_only',
  'feed.feed_prep_donor_mother',
  'feed.feed_prep_full',
  'feed.feed_prep_product',
  // FEED_MANAGEMENT — Feed Administration
  'feed.feed_administer',
  'feed.redosing',
  'feed.quick_feed',
]);
export type FeatureCode = z.infer<typeof FeatureCodeSchema>;

// ── Licensed feature map: { MODULE_CODE → string[] } ────────────────────────

export const LicensedFeatureMapSchema = z.record(ModuleCodeSchema, z.array(z.string()));
export type LicensedFeatureMap = z.infer<typeof LicensedFeatureMapSchema>;

// ── Per-module access context (used inside SessionContext) ───────────────────

export const ModuleAccessContextSchema = z.object({
  rbac_access: z.boolean(),
  features: z.array(z.string()),
});
export type ModuleAccessContext = z.infer<typeof ModuleAccessContextSchema>;

// ── Session context — written by Pre Token Generation Lambda on every login
//    and every 5-min Cognito silent refresh. Matches handler.ts exactly. ──────

export const SessionContextSchema = z.object({
  tenant_id:          z.string(),
  plan_code:          z.string(),                         // STARTER | PROFESSIONAL | ENTERPRISE
  role_code:          z.string(),                         // primary role of the user
  licensed_modules:   z.array(z.string()),                // modules unlocked by the plan
  /**
   * Flat boolean map from ROLE_MODULE_MATRIX for this role.
   * Keys are module-level ("PATIENT_MANAGEMENT") or feature-level ("patient.mother").
   */
  role_module_access: z.record(z.string(), z.boolean()),
  /**
   * Combined plan × RBAC view.
   * Only modules present in licensed_modules appear here.
   */
  accessible_modules: z.record(z.string(), ModuleAccessContextSchema),
});
export type SessionContext = z.infer<typeof SessionContextSchema>;
